import {
  IAccountComment,
  IAnonymousUser,
  IUserDataForMemberManagement,
  IUserLeaderboardPreferenceViewResult,
  IUserNames,
  WorkoutImportOrigin,
} from 'fitbeat.models';
import { UserProfile } from '../../../entities/user/userprofile';
import { IUserProfileRepository } from '../interfaces/iuserprofile-repository';

// cached for the duration of the process
// todo we need a daily cache invalidation or other strategy
export class CachedUserRepository implements IUserProfileRepository {
    private static Cached: UserProfile[];

    constructor(private underlyingExerciseRepository: IUserProfileRepository) { }

    public async setUp(): Promise<CachedUserRepository> {
        CachedUserRepository.Cached = await this.underlyingExerciseRepository.getAll();
        return this;
    }

    public async getAll(): Promise<UserProfile[]> {
        if (!CachedUserRepository.Cached) {
            CachedUserRepository.Cached = await this.underlyingExerciseRepository.getAll();
        }
        return CachedUserRepository.Cached;
    }

    public async get(id: string): Promise<UserProfile | undefined> {
        if (!CachedUserRepository.Cached) {
            CachedUserRepository.Cached = await this.underlyingExerciseRepository.getAll();
        }
        return CachedUserRepository.Cached.find((e: UserProfile) => String(e.uid) === String(id));
    }

    public getMemberInfoByGymName(gymName?: string | undefined, emailExclusionFilter?: string | undefined)
    : Promise<IUserDataForMemberManagement[]> {
        throw new Error('Method not implemented.');
    }

    public getUserProfileOrThrow(uid: string): Promise<UserProfile> {
      throw new Error('Method is not supported on a cached repo');
    }

  public async getByContinueSignupToken(token: string): Promise<UserProfile | undefined> {
    throw new Error('getByContinueSignupToken is not implemented on a cached repo');
  }

    public async add(user: UserProfile): Promise<void> {
        throw new Error('Add is not supported on a cached repo');
    }

    public async update(user: UserProfile): Promise<UserProfile> {
        throw new Error('Update is not supported on a cached repo');
    }

    public async getUsersByEmails(emails: string[]): Promise<UserProfile[]> {
        throw new Error('Method is not supported on a cached repo');
    }

    public async search(searchQuery: string): Promise<UserProfile[]> {
        if (!CachedUserRepository.Cached) {
            CachedUserRepository.Cached = await this.underlyingExerciseRepository.getAll();
        }
        return (CachedUserRepository.Cached.filter((user: UserProfile) =>
            (user.username && user.username.toLowerCase().startsWith(searchQuery.toLowerCase())) ||
            (user.fullname && user.fullname.toLowerCase().startsWith(searchQuery.toLowerCase())) ||
            (user.nickname && user.nickname.toLowerCase().startsWith(searchQuery.toLowerCase()))));
    }

    public getByStudioBeltNumber(beltNumber: string): Promise<UserProfile[]> {
        return Promise.resolve([]);
    }

    public async getUserByEmail(email: string): Promise<UserProfile | undefined> {
        if (!CachedUserRepository.Cached) {
            CachedUserRepository.Cached = await this.underlyingExerciseRepository.getAll();
        }

        return CachedUserRepository.Cached.find((user: UserProfile) => {
            return user.email === email;
        });
    }

    public async getUserByWorkoutImportConfig(
      origin: WorkoutImportOrigin,
      userId: string,
    ): Promise<UserProfile> {
      return this.underlyingExerciseRepository.getUserByWorkoutImportConfig(origin, userId);
    }

    public async getUsersByGymName(gymName?: string, emailExclusionFilter?: string): Promise<UserProfile[]> {
        throw new Error('Get user by gym name is not supported on a cached repo');
    }

    public getUsersByFilters(
        emailExclusionFilter?: string, planIds?: string[], includeOldAccounts?: boolean): Promise<UserProfile[]> {
        throw new Error('Get users by filters is not supported on a cached repo');
    }

    public async getUsersByIds(userprofileIds: string[]): Promise<UserProfile[]> {
        throw new Error('Method not implemented.');
    }

  public getPotentiallyDeletedUsersByIds(userprofileIds: string[]): Promise<(UserProfile | IAnonymousUser)[]> {
    throw new Error('Method not implemented.');
  }

    public removeContinueSignupToken(userId: string): Promise<UserProfile> {
        throw new Error('Method not implemented.');
    }

    public updateAccountComment(userId: string, accountComment: IAccountComment): Promise<IAccountComment[]> {
        throw new Error('Method not implemented.');
    }

    public updateFollowUpDate(userId: string, followUpDate: Date): Promise<Date | undefined> {
        throw new Error('Method not implemented.');
    }

    public getFollowUpDate(userId: string): Promise<Date | undefined> {
        throw new Error('Method not implemented.');
    }

    public async removeFollowUpDate(userId: string): Promise<void> {
        throw new Error('Method not implemented');
    }

    public getAllUsersInFamilyPlan(planOwnerUserProfileId: string): Promise<UserProfile[]> {
      throw new Error('Method not implemented.');
    }

    public getSingleUserForTesting(): Promise<any> {
      throw new Error('Method not implemented.');
    }

    public getUserByEnrolledFaceId(enrolledFaceId: string): Promise<UserProfile | undefined> {
      throw new Error('Method not implemented.');
    }

    public getUsersByUtmTagSource(source: string, medium: string): Promise<UserProfile[]> {
        throw new Error('Method not implemented.');
      }

    public getUsersSignedUpAfter(requestTime: Date, signupDate: Date, gymName: string): Promise<UserProfile[]> {
        throw new Error('Method not implemented.');
    }

  public getAllUsersInTeam(team: string): Promise<IUserNames[]> {
    return this.underlyingExerciseRepository.getAllUsersInTeam(team);
  }

  public getLeaderboardPreferencesForUsersByLeaderboardId(
    leaderboardId: string,
  ): Promise<IUserLeaderboardPreferenceViewResult[]> {
    return this.underlyingExerciseRepository.getLeaderboardPreferencesForUsersByLeaderboardId(leaderboardId);
  }
}
